/**
 * ChatHeader - Shows and allows editing of the session title
 *
 * The title is editable inline. Clicking it converts to an <input>.
 * On blur or Enter, the updated title is saved via travelAgentService.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  AddComment as AddCommentIcon,
  SmartToy as SmartToyIcon
} from '@mui/icons-material';
import { travelAgentService } from '../../services/travelAgentService.js';

/**
 * ChatHeader Component
 *
 * @param {Object} props
 * @param {string} props.sessionTitle - Current session title
 * @param {string} props.sessionId - Current session ID
 * @param {string} props.effectiveUserId - Trip owner user ID
 * @param {string} props.tripId - Trip identifier
 * @param {Function} props.onNewSession - Create a new session
 */
const ChatHeader = ({ sessionTitle, sessionId, effectiveUserId, tripId, onNewSession }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(sessionTitle || '');
  const inputRef = useRef(null);

  // Sync editValue when sessionTitle changes (e.g. auto-generated)
  useEffect(() => {
    setEditValue(sessionTitle || '');
  }, [sessionTitle]);

  // Focus input when editing starts
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleTitleClick = () => {
    setEditing(true);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    }
    if (e.key === 'Escape') {
      setEditing(false);
      setEditValue(sessionTitle || '');
    }
  };

  const handleTitleBlur = () => {
    commitEdit();
  };

  const commitEdit = async () => {
    setEditing(false);
    const newTitle = editValue.trim();
    if (!newTitle || newTitle === sessionTitle) return;

    try {
      await travelAgentService.updateSessionTitle(effectiveUserId, tripId, sessionId, newTitle);
    } catch (error) {
      console.error('[ChatHeader] Failed to update title:', error);
      setEditValue(sessionTitle || '');
    }
  };

  const titleForDisplay = sessionTitle || 'New conversation';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
        gap: 1,
        flexShrink: 0
      }}
    >
      {/* Agent icon */}
      <SmartToyIcon sx={{ color: 'primary.main', flexShrink: 0 }} />

      {/* Session title — editable inline */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {editing ? (
          <input
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            onBlur={handleTitleBlur}
            style={{
              width: '100%',
              fontSize: '0.9375rem',
              fontWeight: 600,
              border: 'none',
              borderBottom: '2px solid #1976d2',
              outline: 'none',
              background: 'transparent',
              padding: '2px 0',
              color: 'inherit'
            }}
            aria-label="Edit session title"
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              '&:hover .edit-icon': { opacity: 1 }
            }}
            onClick={handleTitleClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTitleClick();
              }
            }}
            role="button"
            tabIndex={0}
            title="Click to edit session title"
          >
            <Typography
              variant="body1"
              fontWeight={600}
              noWrap
              sx={{ maxWidth: 300 }}
            >
              {titleForDisplay}
            </Typography>
            <EditIcon
              className="edit-icon"
              sx={{ fontSize: '0.875rem', opacity: 0, color: 'text.secondary', transition: 'opacity 0.15s' }}
            />
          </Box>
        )}
      </Box>

      {/* New session button */}
      <Tooltip title="New conversation">
        <IconButton size="small" onClick={onNewSession} aria-label="Start new conversation">
          <AddCommentIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ChatHeader;
